import { useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Public legal page (no login): Termeni de utilizare, Politica de
 * confidențialitate and Ștergerea contului, each with an anchor so Google Play
 * can link the privacy policy (#confidentialitate) and the account deletion
 * page (#stergere-cont). Served by every instance; the canonical address is
 * https://app.firmio.ro/terms. Romanian only, like the contracts.
 */

const COMPANY = {
  name: 'CLEAN CODE CULTURE S.R.L.',
  cui: '47847495',
  regCom: 'J2023001903239',
  address: 'Str. Maior Alexandru Câmpeanu 4-6, Sector 1, București',
  email: 'support@firmio.ro',
};

const CONTACT_EMAIL = (
  <a href={`mailto:${COMPANY.email}`} className="underline" style={{ color: '#C4B5FD' }}>
    {COMPANY.email}
  </a>
);

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-6 space-y-4">
      <h2
        className="text-2xl font-bold"
        style={{ color: '#F1F5F9', fontFamily: "'Bricolage Grotesque', sans-serif" }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function H3({ children }: { children: ReactNode }) {
  return (
    <h3 className="pt-2 text-base font-semibold" style={{ color: '#E2E8F0' }}>
      {children}
    </h3>
  );
}

function List({ children }: { children: ReactNode }) {
  return <ul className="list-disc space-y-1.5 pl-5">{children}</ul>;
}

export function TermsPage() {
  const { hash } = useLocation();

  // React Router does not scroll to an anchor on its own.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <div className="min-h-screen" style={{ background: '#0A0E1A', color: '#CBD5E1' }}>
      <div className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
        <header className="mb-10 space-y-6">
          <Link to="/login" aria-label="Firmio">
            <img
              src="/brand/firmio-logo-horizontal-on-dark.svg"
              alt="Firmio"
              className="h-12 w-auto"
            />
          </Link>
          <div>
            <h1
              className="text-3xl font-bold sm:text-4xl"
              style={{ color: '#F1F5F9', fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              Termeni și confidențialitate
            </h1>
            <p className="mt-2 text-sm" style={{ color: '#94A3B8' }}>
              În vigoare de la 2 noiembrie 2026.
            </p>
          </div>
          <nav className="flex flex-wrap gap-2 text-sm">
            {[
              ['#termeni', 'Termeni de utilizare'],
              ['#confidentialitate', 'Politica de confidențialitate'],
              ['#stergere-cont', 'Ștergerea contului'],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                className="rounded-full px-3 py-1.5 hover:underline"
                style={{ border: '1px solid rgba(148, 163, 184, 0.2)', color: '#C4B5FD' }}
              >
                {label}
              </a>
            ))}
          </nav>
        </header>

        <div className="space-y-14 text-[15px] leading-relaxed">
          <Section id="termeni" title="Termeni de utilizare">
            <p>
              Firmio este o aplicație de management pentru firme (proiecte, sarcini, echipă,
              clienți, facturare, întâlniri, chat și asistent AI), disponibilă pe web, ca aplicație
              mobilă și ca aplicație desktop (Windows, macOS). Firmio este dezvoltat și operat de{' '}
              {COMPANY.name}, CUI {COMPANY.cui}, Reg. Com. {COMPANY.regCom}, cu sediul în{' '}
              {COMPANY.address} („noi”).
            </p>
            <H3>Cine folosește Firmio</H3>
            <p>
              Firmio se vinde firmelor („clientul”). Fiecare client primește o instanță proprie, iar
              conturile utilizatorilor sunt create sau aprobate de administratorii clientului.
              Folosind Firmio, utilizatorul acceptă acești termeni și regulile interne ale firmei
              pentru care lucrează.
            </p>
            <H3>Utilizare acceptabilă</H3>
            <List>
              <li>Nu împărțiți contul și nu încercați să accesați date la care nu aveți drept.</li>
              <li>Nu încărcați conținut ilegal, malware sau date personale fără un temei legal.</li>
              <li>
                Nu încercați să ocoliți măsurile de securitate și nu supraîncărcați serviciul.
              </li>
            </List>
            <H3>Conținutul vostru</H3>
            <p>
              Datele introduse în Firmio aparțin clientului. Le folosim doar ca să furnizăm
              serviciul, conform contractului cu clientul și politicii de confidențialitate de mai
              jos.
            </p>
            <H3>Funcțiile AI</H3>
            <p>
              Asistentul AI, rezumatele, transcrierile și sugestiile sunt generate automat și pot
              conține greșeli. Verificați-le înainte de a le folosi în decizii, documente sau
              comunicări oficiale.
            </p>
            <H3>Disponibilitate și răspundere</H3>
            <p>
              Ne străduim ca Firmio să fie disponibil și sigur, dar serviciul este furnizat „ca
              atare”. Răspunderea noastră față de client este cea stabilită în contractul cu acesta.
              Putem suspenda accesul în caz de abuz, de risc de securitate sau la cererea
              clientului.
            </p>
            <H3>Modificări și legea aplicabilă</H3>
            <p>
              Putem actualiza acești termeni; versiunea curentă este întotdeauna pe această pagină,
              cu data intrării în vigoare. Termenii sunt guvernați de legea română.
            </p>
          </Section>

          <Section id="confidentialitate" title="Politica de confidențialitate">
            <H3>Cine răspunde de date</H3>
            <p>
              Pentru datele unei firme client (angajații, clienții și documentele ei), firma client
              este <strong>operatorul</strong> datelor, iar {COMPANY.name} este{' '}
              <strong>persoana împuternicită</strong>, care prelucrează datele în numele și după
              instrucțiunile clientului. Pentru instanța proprie a {COMPANY.name} și pentru datele
              de facturare ale clienților noștri, noi suntem operatorul.
            </p>
            <p>
              Contact pentru protecția datelor: {CONTACT_EMAIL}, {COMPANY.name}, {COMPANY.address}.
            </p>

            <H3>Ce date prelucrăm</H3>
            <List>
              <li>
                <strong>Cont:</strong> nume, email, parolă (stocată doar ca hash), rol, poză de
                profil, data ultimei autentificări.
              </li>
              <li>
                <strong>Echipă și resurse umane</strong> (introduse de firmă): funcție, departament,
                telefon, tarif orar, date de contract, IBAN, contracte de muncă, salarii și
                contribuții, cereri de concediu.{' '}
                <strong>
                  Concediile medicale pot include certificate medicale, adică date privind
                  sănătatea.
                </strong>
              </li>
              <li>
                <strong>Activitate:</strong> proiecte, sarcini, comentarii, fișiere, mesaje din
                chatul echipei, conversații cu asistentul AI, notițe și minute de ședință.
              </li>
              <li>
                <strong>Întâlniri și apeluri:</strong> audio și video în timp real, înregistrări
                (când cineva pornește înregistrarea), transcrieri automate, numele și emailul
                invitaților din Firmio Meet.
              </li>
              <li>
                <strong>Clienți, furnizori și financiar:</strong> date de contact, facturi, extrase
                și tranzacții bancare.
              </li>
              <li>
                <strong>Integrări conectate de utilizator:</strong> evenimente din Google Calendar,
                emailuri din Gmail (citire și ciorne), fișiere din Google Drive.
              </li>
              <li>
                <strong>WhatsApp</strong> (doar dacă firma activează modulul): mesajele din
                grupurile WhatsApp pe care firma le are cu clienții ei — expeditor, dată, text și
                tipul fișierelor (fără fișierele în sine). Sunt citite de pe numărul de WhatsApp al
                firmei, conectat ca dispozitiv, și păstrate pe serverul instanței.
              </li>
              <li>
                <strong>Tehnice:</strong> tokenuri pentru notificări push, tipul dispozitivului,
                jurnale de server (adresă IP, browser) folosite pentru securitate și depanare.
              </li>
            </List>
            <p>
              Nu folosim cookie-uri de urmărire, analytics sau reclame și nu colectăm locația,
              contactele telefonului, CNP-ul sau acte de identitate.
            </p>

            <H3>De ce le prelucrăm</H3>
            <List>
              <li>
                Ca să furnizăm serviciul comandat de client (executarea contractului; pentru datele
                angajaților, temeiul este cel stabilit de angajator ca operator).
              </li>
              <li>Securitate, prevenirea abuzurilor și depanare (interes legitim).</li>
              <li>Facturare și obligații fiscale (obligație legală).</li>
              <li>
                Notificări (push, email) despre activitatea din cont, pe care fiecare le poate regla
                din Profil.
              </li>
            </List>

            <H3>Cu cine le împărțim</H3>
            <p>
              Folosim furnizori (persoane împuternicite) doar pentru funcțiile care au nevoie de ei;
              o parte se activează doar dacă firma client le configurează:
            </p>
            <List>
              <li>
                <strong>Hostinger</strong> — găzduirea serverelor.
              </li>
              <li>
                <strong>Google</strong> — stocarea fișierelor și a copiilor de siguranță (Google
                Drive), Calendar, Gmail și Drive conectate de utilizator, notificări push pe Android
                (Firebase Cloud Messaging), fonturi.
              </li>
              <li>
                <strong>Anthropic</strong> — asistentul AI, rezumate, minute de ședință și
                procesarea documentelor încărcate.
              </li>
              <li>
                <strong>OpenAI</strong> — transcrierea întâlnirilor și apelurilor, căutarea
                semantică în baza de cunoștințe, asistentul vocal.
              </li>
              <li>
                <strong>LiveKit</strong> — transmisia audio/video pentru apeluri și Firmio Meet.
              </li>
              <li>
                <strong>Expo</strong> — livrarea notificărilor push către aplicația mobilă.
              </li>
              <li>
                <strong>GitHub</strong> — găzduirea versiunilor aplicației desktop; aplicația
                verifică periodic dacă există o versiune nouă (cererea nu conține date despre dvs.).
              </li>
              <li>
                <strong>Furnizorul de email</strong> configurat de firmă — trimiterea emailurilor
                (invitații, resetare parolă, rezumate); emailurile pot conține un pixel care arată
                dacă au fost deschise.
              </li>
              <li>
                <strong>SmartBill și ANAF (e-Factura)</strong> — facturare, când firma le
                conectează.
              </li>
              <li>
                <strong>Tavily / SerpAPI</strong> — căutări web despre participanții la o întâlnire,
                doar când cineva cere explicit o documentare.
              </li>
              <li>
                <strong>WhatsApp (Meta)</strong> — sursa conversațiilor sincronizate, când firma
                conectează un număr; Firmio doar citește grupurile legate de clienți.
              </li>
            </List>
            <p>
              Unii furnizori (Anthropic, OpenAI, Google) prelucrează date și în afara Spațiului
              Economic European, în special în SUA. Transferurile se fac pe baza clauzelor
              contractuale standard ale Comisiei Europene sau a cadrului UE–SUA privind protecția
              datelor. Conform termenilor comerciali ai Anthropic și OpenAI, datele trimise prin
              API-urile lor nu sunt folosite pentru antrenarea modelelor. Nu vindem date personale.
            </p>

            <H3>Cât timp le păstrăm</H3>
            <p>
              Datele contului și ale activității se păstrează cât timp firma client folosește Firmio
              sau până când sunt șterse de un administrator. Copiile de siguranță se rotesc automat
              și se șterg după cel mult 30 de zile. Documentele financiare se păstrează cât cere
              legea. La încetarea contractului, datele clientului se șterg sau se predau clientului,
              conform contractului.
            </p>

            <H3>Drepturile voastre</H3>
            <p>
              Aveți dreptul de acces, rectificare, ștergere, restricționare, portabilitate și
              opoziție, precum și dreptul de a nu face obiectul unei decizii bazate exclusiv pe
              prelucrare automată. Dacă sunteți angajatul unei firme client, adresați-vă întâi
              firmei (operatorul); vă putem scrie și nouă, la {CONTACT_EMAIL}, și redirecționăm
              cererea. Răspundem în cel mult 30 de zile. Puteți depune plângere la Autoritatea
              Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal
              (www.dataprotection.ro).
            </p>

            <H3>Securitate</H3>
            <p>
              Conexiunile sunt criptate (HTTPS), parolele sunt stocate ca hash, cheile de integrare
              și secretele sunt criptate (AES-256-GCM), iar accesul în aplicație este limitat prin
              roluri și permisiuni stabilite de firmă.
            </p>

            <H3>Aplicația mobilă</H3>
            <List>
              <li>
                <strong>Camera și microfonul</strong> — doar pentru apeluri, întâlniri video,
                transcriere și fotografii pe care alegeți să le atașați.
              </li>
              <li>
                <strong>Galeria</strong> — doar pentru imaginile pe care le atașați.
              </li>
              <li>
                <strong>Notificări</strong> — mesaje, sarcini, întâlniri; se reglează din Profil.
              </li>
              <li>
                <strong>Bluetooth și servicii în prim-plan</strong> — căști audio, apeluri și
                partajarea ecranului care continuă când aplicația e în fundal.
              </li>
            </List>

            <H3>Aplicația desktop</H3>
            <List>
              <li>
                <strong>Camera, microfonul și ecranul</strong> — doar pentru apeluri, întâlniri
                video și partajarea ecranului sau a ferestrei pe care o alegeți.
              </li>
              <li>
                <strong>Notificări de sistem</strong> — mesaje, sarcini, întâlniri, și cu fereastra
                închisă; se reglează din Profil sau se opresc din meniul pictogramei Firmio.
              </li>
              <li>
                <strong>Pornire la login</strong> — doar dacă o activați din meniul pictogramei.
              </li>
            </List>

            <H3>Stocare locală</H3>
            <p>
              Aplicația păstrează pe dispozitiv tokenul de autentificare și preferințe (temă, limbă,
              vizualizări). Pe telefon, aceste date sunt criptate. Aplicația desktop le păstrează în
              profilul ei de pe computer, împreună cu organizația aleasă. Nu folosim cookie-uri.
            </p>

            <H3>Minori</H3>
            <p>Firmio este destinat firmelor și nu se adresează persoanelor sub 16 ani.</p>

            <H3>Modificări</H3>
            <p>
              Când politica se schimbă, actualizăm data de mai sus; pentru schimbări importante îi
              anunțăm pe clienți.
            </p>
          </Section>

          <Section id="stergere-cont" title="Ștergerea contului">
            <p>Puteți cere oricând ștergerea contului vostru Firmio, în una din două moduri:</p>
            <List>
              <li>
                <strong>Din aplicație</strong> (web, mobil sau desktop): Profil → „Cere ștergerea
                contului”, apoi confirmați cu parola. Contul este dezactivat imediat și
                administratorii firmei primesc cererea.
              </li>
              <li>
                <strong>Pe email</strong>, la {CONTACT_EMAIL}, de pe adresa contului, cu numele
                firmei (organizației) pentru care folosiți Firmio.
              </li>
            </List>
            <H3>Ce se întâmplă</H3>
            <List>
              <li>
                Contul nu mai poate fi folosit, sesiunile se închid și notificările se opresc
                imediat.
              </li>
              <li>
                Administratorul firmei finalizează ștergerea: numele, emailul, poza, conexiunile la
                alte servicii, preferințele și datele de angajare se șterg sau se anonimizează.
              </li>
              <li>
                Înregistrările de business ale firmei (de exemplu sarcini, facturi, documente) pot
                rămâne la firmă, fără a mai fi legate de identitatea voastră, iar documentele cu
                obligație legală de păstrare se păstrează pe durata cerută de lege.
              </li>
              <li>
                Copiile de siguranță care conțin datele se șterg automat în cel mult 30 de zile.
              </li>
              <li>Cererea este soluționată în cel mult 30 de zile.</li>
            </List>
          </Section>
        </div>

        <footer
          className="mt-16 border-t pt-6 text-xs"
          style={{ borderColor: 'rgba(148, 163, 184, 0.15)', color: '#64748B' }}
        >
          {COMPANY.name} · CUI {COMPANY.cui} · Reg. Com. {COMPANY.regCom}
        </footer>
      </div>
    </div>
  );
}
